import { useEffect, useRef, useState } from "react";
import { spanishViridian500 } from "@habitech/shared";
import Icon from "src/components/common/Icon";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
    selectCartTotal,
    selectCommercialCartSelection,
    selectSelectedGateway,
    selectSelectedPaymentMethod,
} from "src/redux/shopping_cart/shopping_cart.selectors";
import {
    setMsiEnabled,
    setMsiMonths,
    setSelectedGateway,
    setSelectedPaymentMethod,
} from "src/redux/shopping_cart/shopping_cart.slice";
import {
    Card,
    DropdownList,
    DropdownWrapper,
    Header,
    Label,
    MsiCheckboxRow,
    MsiSelect,
    MsiWrapper,
    Option,
    Trigger,
} from "./PaymentMethodSelect.styles";
import {
    isPaymentMethodDisallowedForTotal,
    methodsName,
    payment_method,
    PAYMENT_GATEWAYS,
} from "src/constants/ShoppingCart";
import { MSI_MIN_AMOUNT, MSI_OPTIONS } from "src/constants/Calculator";

const GATEWAY_PLACEHOLDER = "Selecciona una pasarela";
const FORMA_PLACEHOLDER = "Selecciona una forma de pago";

/** Cuál de los dos dropdowns en cascada está abierto (null = ninguno). */
type OpenDropdown = "gateway" | "forma" | null;

const PaymentMethodSelect = () => {
    const dispatch = useAppDispatch();
    const selectedGateway = useAppSelector(selectSelectedGateway);
    const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
    const { msiEnabled, msiMonths } = useAppSelector(selectCommercialCartSelection);
    const total = useAppSelector(selectCartTotal);
    const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const gatewayConfig = selectedGateway !== null
        ? PAYMENT_GATEWAYS.find((g) => g.gateway === selectedGateway)
        : undefined;
    const formaOptions = gatewayConfig?.formas ?? [];
    // Only multi-forma gateways (OpenPay) show the forma dropdown. Single-forma
    // gateways (MercadoPago, Paypal) and terminal ones (Pago Directo) resolve
    // their payment_method on gateway selection.
    const showFormaDropdown = formaOptions.length > 1;

    const isCard = selectedPaymentMethod === payment_method[methodsName.CARD];
    const isMercadoPago = selectedPaymentMethod === payment_method[methodsName.MERCADOPAGO];
    const paymentOverLimit = isPaymentMethodDisallowedForTotal(selectedPaymentMethod, total);

    const availableMsiOptions = MSI_OPTIONS.filter((m) => total >= MSI_MIN_AMOUNT[m]);
    const showMsi = (isCard || isMercadoPago) && availableMsiOptions.length > 0 && !paymentOverLimit;

    useEffect(() => {
        if (!showMsi) {
            if (msiEnabled) dispatch(setMsiEnabled(false));
            return;
        }
        if (msiMonths !== null && total < (MSI_MIN_AMOUNT[msiMonths] ?? 0)) {
            dispatch(setMsiMonths(null));
        }
    }, [total, showMsi, msiEnabled, msiMonths, dispatch]);

    const gatewayDisplayLabel = gatewayConfig ? gatewayConfig.label : GATEWAY_PLACEHOLDER;
    const isGatewayPlaceholder = selectedGateway === null;

    const formaSelectedOption = selectedPaymentMethod !== null
        ? formaOptions.find((f) => f.value === selectedPaymentMethod)
        : null;
    const formaDisplayLabel = formaSelectedOption ? formaSelectedOption.label : FORMA_PLACEHOLDER;
    const isFormaPlaceholder = selectedPaymentMethod === null;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpenDropdown(null);
            }
        };
        if (openDropdown !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdown]);

    const handleSelectGateway = (gateway: typeof PAYMENT_GATEWAYS[number]) => {
        dispatch(setSelectedGateway(gateway.gateway));
        const autoMethod = gateway.terminalPaymentMethod
            ?? (gateway.formas.length === 1 ? gateway.formas[0].value : undefined);
        if (autoMethod !== undefined) {
            dispatch(setSelectedPaymentMethod(autoMethod));
        }
        setOpenDropdown(null);
    };

    const handleSelectForma = (value: number) => {
        dispatch(setSelectedPaymentMethod(value));
        setOpenDropdown(null);
    };

    return (
        <Card ref={containerRef}>
            <Header>
                <Icon icon="pcom-icon-credit-card-2" size={22} style={{ color: spanishViridian500 }} />
                <h2 className="header-title">Método de Pago</h2>
            </Header>

            <Label htmlFor="payment-gateway-trigger">Pasarela de pago</Label>
            <DropdownWrapper>
                <Trigger
                    id="payment-gateway-trigger"
                    type="button"
                    className={isGatewayPlaceholder ? "placeholder" : ""}
                    onClick={() => setOpenDropdown((prev) => (prev === "gateway" ? null : "gateway"))}
                    aria-haspopup="listbox"
                    aria-expanded={openDropdown === "gateway"}
                >
                    <span>{gatewayDisplayLabel}</span>
                    <Icon
                        icon="pcom-icon-chevron-down"
                        size={16}
                        style={{
                            transform: openDropdown === "gateway" ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s ease",
                        }}
                    />
                </Trigger>
                {openDropdown === "gateway" && (
                    <DropdownList role="listbox">
                        {PAYMENT_GATEWAYS.map((gateway) => (
                            <Option
                                key={gateway.gateway}
                                role="option"
                                aria-selected={selectedGateway === gateway.gateway}
                                $selected={selectedGateway === gateway.gateway}
                                onClick={() => handleSelectGateway(gateway)}
                            >
                                {gateway.label}
                            </Option>
                        ))}
                    </DropdownList>
                )}
            </DropdownWrapper>

            {showFormaDropdown && (
                <>
                    <Label htmlFor="payment-forma-trigger">Forma de pago</Label>
                    <DropdownWrapper>
                        <Trigger
                            id="payment-forma-trigger"
                            type="button"
                            className={isFormaPlaceholder ? "placeholder" : ""}
                            onClick={() => setOpenDropdown((prev) => (prev === "forma" ? null : "forma"))}
                            aria-haspopup="listbox"
                            aria-expanded={openDropdown === "forma"}
                        >
                            <span>{formaDisplayLabel}</span>
                            <Icon
                                icon="pcom-icon-chevron-down"
                                size={16}
                                style={{
                                    transform: openDropdown === "forma" ? "rotate(180deg)" : "none",
                                    transition: "transform 0.2s ease",
                                }}
                            />
                        </Trigger>
                        {openDropdown === "forma" && (
                            <DropdownList role="listbox">
                                {formaOptions.map((forma) => (
                                    <Option
                                        key={forma.value}
                                        role="option"
                                        aria-selected={selectedPaymentMethod === forma.value}
                                        $selected={selectedPaymentMethod === forma.value}
                                        onClick={() => handleSelectForma(forma.value)}
                                    >
                                        {forma.label}
                                    </Option>
                                ))}
                            </DropdownList>
                        )}
                    </DropdownWrapper>
                </>
            )}

            {showMsi && (
                <MsiWrapper>
                    <MsiCheckboxRow>
                        <input
                            type="checkbox"
                            checked={msiEnabled}
                            onChange={(e) => dispatch(setMsiEnabled(e.target.checked))}
                            aria-label="MSI"
                        />
                        MSI
                    </MsiCheckboxRow>
                    {msiEnabled && (
                        <MsiSelect
                            value={msiMonths ?? ""}
                            onChange={(e) =>
                                dispatch(setMsiMonths(e.target.value ? Number(e.target.value) : null))
                            }
                            aria-label="Meses MSI"
                        >
                            <option value="" disabled>Selecciona meses</option>
                            {availableMsiOptions.map((m) => (
                                <option key={m} value={m}>
                                    {m} meses
                                </option>
                            ))}
                        </MsiSelect>
                    )}
                </MsiWrapper>
            )}
        </Card>
    );
};

export default PaymentMethodSelect;
